import { Location } from '@cobliteam/common-types';
import { AiRecommendationJourney, permissionsConfig } from '@cobliteam/hooks/src/useAiRecommendationsStore/useAiRecommendationsStore';
import type { Permission } from '@cobliteam/permissions';
import { BellRing, ClipboardCheck, Link2, type LucideIcon, MapPin, Users } from 'lucide-react';

/**
 * Minimum number of records a fleet must have for each Basic Value feature.
 * Below this threshold the guided onboarding dialog shows the feature as pending.
 */
export const BASIC_VALUE_MIN_COUNT = 2;

export type BasicValueFeatureId = 'drivers' | 'driver-association' | 'geofences' | 'alerts' | 'checklists';

export interface BasicValueFeatureConfig {
  id: BasicValueFeatureId;
  /** Page the CTA navigates to so the user can resolve the pending registration. */
  route: string;
  /** Permission required to resolve the pending registration (reuses the Basic Value journeys config). */
  permission: Permission;
  icon: LucideIcon;
  /** When true, the step can't be completed inside the modal — it navigates to a page. These are sorted last. */
  leadsToPage?: boolean;
  /** CSS selector of the button the cursor flow should guide the user to on the destination page. */
  tourTargetSelector?: string;
}

export const BASIC_VALUE_FEATURES: BasicValueFeatureConfig[] = [
  {
    id: 'drivers',
    route: Location.DRIVER_MANAGEMENT,
    permission: permissionsConfig[AiRecommendationJourney.DRIVER_REGISTRATION],
    icon: Users,
  },
  {
    id: 'driver-association',
    route: Location.DRIVER_MANAGEMENT,
    permission: permissionsConfig[AiRecommendationJourney.SETUP_DRIVER_ASSOCIATION],
    icon: Link2,
    // Resolved by navigating to the drivers page + a guided cursor flow (not inline in the modal).
    leadsToPage: true,
  },
  {
    id: 'geofences',
    route: Location.GEOFENCES_CREATE,
    permission: permissionsConfig[AiRecommendationJourney.GEOFENCES],
    icon: MapPin,
    leadsToPage: true,
    tourTargetSelector: '#submitGeofenceForm',
  },
  {
    id: 'alerts',
    route: Location.ALERTS_TRIGGERS_CREATE,
    permission: permissionsConfig[AiRecommendationJourney.ALERTS],
    icon: BellRing,
  },
  {
    id: 'checklists',
    route: `${Location.CHECKLIST_ALL}?create=true&template=daily_exit`,
    permission: permissionsConfig[AiRecommendationJourney.CHECKLISTS],
    icon: ClipboardCheck,
  },
];

export const BASIC_VALUE_FEATURE_BY_ID: Record<BasicValueFeatureId, BasicValueFeatureConfig> = BASIC_VALUE_FEATURES.reduce(
  (acc, feature) => {
    acc[feature.id] = feature;
    return acc;
  },
  {} as Record<BasicValueFeatureId, BasicValueFeatureConfig>
);

/** Maps an AI recommendation journeyId to the Basic Value feature it drives (when one exists). */
export const JOURNEY_TO_FEATURE: Partial<Record<string, BasicValueFeatureId>> = {
  [AiRecommendationJourney.DRIVER_REGISTRATION]: 'drivers',
  [AiRecommendationJourney.SETUP_DRIVER_ASSOCIATION]: 'driver-association',
  [AiRecommendationJourney.GEOFENCES]: 'geofences',
  [AiRecommendationJourney.ALERTS]: 'alerts',
  [AiRecommendationJourney.CHECKLISTS]: 'checklists',
};
